// What the Links card says about each GitHub reference beside its number:
// whether it is a pull request or an issue, its title and its state, and a
// commit's subject. One GraphQL request asks for all of a card's
// references at once (a `repository` per repository, an alias per
// reference), and answers are kept for a while across pull requests.
import type { LinkGroup } from './links';

export type RefState = 'open' | 'merged' | 'closed' | 'draft';

export type RefDetail = { kind: 'pull' | 'issue'; title: string; state: RefState } | { kind: 'commit'; title: string };

/** One thing to ask about, keyed as the Links card keys its entry. */
export type RefRequest =
  | { key: string; kind: 'ref'; owner: string; repo: string; number: number }
  | { key: string; kind: 'commit'; owner: string; repo: string; sha: string };

/** A GraphQL error, as much of it as is read here. */
type GqlError = { type?: string; path?: readonly (string | number)[]; message?: string };

/** GitHub's errors that say a reference is not there for the viewer: an answer, kept like one. */
const KNOWN_ABSENT = new Set(['NOT_FOUND', 'FORBIDDEN']);

/** The largest number GraphQL's `Int` holds: a larger `#N` would fail the whole request. */
const MAX_INT = 2 ** 31 - 1;

const ISSUE_OR_PR = `__typename ... on Issue { title state } ... on PullRequest { title state isDraft }`;

interface IssueOrPr {
  __typename: 'Issue' | 'PullRequest';
  title: string;
  state: 'OPEN' | 'CLOSED' | 'MERGED';
  isDraft?: boolean;
}

/**
 * The request for `reqs`: one `repository` field per repository (owner
 * and name passed as variables), inside it `iN: issueOrPullRequest` per
 * reference and `cN: object(expression:)` per commit, N being the
 * request's index. `read` maps an answer back to each key: its detail, or
 * null where GitHub gave none (no such reference, or no access to it). A
 * key whose field or repository has any other error (a timeout, GitHub's
 * own failure) is left out: not known yet.
 */
export function refDetailsQuery(reqs: readonly RefRequest[]): {
  query: string;
  variables: Record<string, unknown>;
  read: (data: Record<string, unknown> | null | undefined, errors?: readonly GqlError[]) => Map<string, RefDetail | null>;
} {
  const repos = new Map<string, { alias: string; owner: string; repo: string; fields: string[] }>();
  const where: { req: RefRequest; repo: string; field: string }[] = [];
  const vars: Record<string, unknown> = {};
  const decls: string[] = [];
  reqs.forEach((req, i) => {
    const id = `${req.owner}/${req.repo}`.toLowerCase();
    let r = repos.get(id);
    if (!r) {
      const n = repos.size;
      r = { alias: `r${n}`, owner: req.owner, repo: req.repo, fields: [] };
      repos.set(id, r);
      vars[`o${n}`] = req.owner;
      vars[`n${n}`] = req.repo;
      decls.push(`$o${n}: String!`, `$n${n}: String!`);
    }
    if (req.kind === 'ref') {
      r.fields.push(`i${i}: issueOrPullRequest(number: ${Math.trunc(req.number)}) { ${ISSUE_OR_PR} }`);
      where.push({ req, repo: r.alias, field: `i${i}` });
    } else {
      vars[`e${i}`] = req.sha;
      decls.push(`$e${i}: String!`);
      r.fields.push(`c${i}: object(expression: $e${i}) { ... on Commit { messageHeadline } }`);
      where.push({ req, repo: r.alias, field: `c${i}` });
    }
  });
  const body = [...repos.values()].map((r, n) => `  ${r.alias}: repository(owner: $o${n}, name: $n${n}) {\n    ${r.fields.join('\n    ')}\n  }`).join('\n');
  const query = `query RefDetails(${decls.join(', ')}) {\n${body}\n}`;
  const read = (data: Record<string, unknown> | null | undefined, errors: readonly GqlError[] = []) => {
    const out = new Map<string, RefDetail | null>();
    const transient = new Set(errors.filter((e) => !KNOWN_ABSENT.has(e.type ?? '')).map((e) => (e.path ?? []).slice(0, 2).join('.')));
    for (const w of where) {
      if (transient.has(w.repo) || transient.has(`${w.repo}.${w.field}`)) continue;
      const repo = data?.[w.repo] as Record<string, unknown> | null | undefined;
      const node = repo?.[w.field] as (IssueOrPr & { messageHeadline?: string }) | null | undefined;
      out.set(w.req.key, node ? detailOf(w.req, node) : null);
    }
    return out;
  };
  return { query, variables: vars, read };
}

function detailOf(req: RefRequest, node: IssueOrPr & { messageHeadline?: string }): RefDetail | null {
  if (req.kind === 'commit') return typeof node.messageHeadline === 'string' ? { kind: 'commit', title: node.messageHeadline } : null;
  if (node.__typename !== 'Issue' && node.__typename !== 'PullRequest') return null;
  const pull = node.__typename === 'PullRequest';
  const state: RefState = node.state === 'MERGED' ? 'merged' : node.state === 'CLOSED' ? 'closed' : pull && node.isDraft ? 'draft' : 'open';
  return { kind: pull ? 'pull' : 'issue', title: node.title, state };
}

/** A reference's kind and state as the card shows them: `PR · merged`, `issue · open`; null for a commit. */
export function refStateText(d: RefDetail): string | null {
  return d.kind === 'commit' ? null : `${d.kind === 'pull' ? 'PR' : 'issue'} · ${d.state}`;
}

/** What to ask about for a card's entries: every reference, and each commit whose subject is not known. */
export function refRequests(groups: readonly LinkGroup[]): RefRequest[] {
  const out: RefRequest[] = [];
  for (const g of groups)
    for (const e of g.entries) {
      const t = e.target;
      if (t.kind === 'ref' && (t.number < 1 || t.number > MAX_INT)) continue;
      if (t.kind === 'ref') out.push({ key: e.key, kind: 'ref', owner: t.owner, repo: t.repo, number: t.number });
      else if (t.kind === 'commit' && !e.title) out.push({ key: e.key, kind: 'commit', owner: t.owner, repo: t.repo, sha: t.sha });
    }
  return out;
}

/** Runs a GraphQL document, returning what data came back (`graphqlResult` in the app). */
export type RunPartial = (
  query: string,
  variables: Record<string, unknown>,
) => Promise<{ data: unknown; errors?: readonly GqlError[] }>;

/**
 * Details by key, kept TTL_MS (null: GitHub gave none, kept as long). A
 * failed request leaves its keys unknown, not asked again for RETRY_MS.
 */
export class RefDetails {
  /** How long an answer is used before it is asked for again. */
  static TTL_MS = 10 * 60_000;
  /** How long after a failed request its keys wait before they are asked for again. */
  static RETRY_MS = 60_000;
  /** References per request. */
  static BATCH = 50;
  /** Keys kept at most. */
  static MAX = 1000;

  private held = new Map<string, { detail: RefDetail | null; at: number }>();
  private failedAt = new Map<string, number>();
  private asking = new Set<string>();

  private now: () => number;

  constructor(now: () => number = Date.now) {
    this.now = now;
  }

  /** The detail held for `key`: null when GitHub gave none, undefined when not (yet) known. */
  get(key: string): RefDetail | null | undefined {
    return this.held.get(key)?.detail;
  }

  private wanted(r: RefRequest): boolean {
    if (this.asking.has(r.key)) return false;
    const h = this.held.get(r.key);
    if (h && this.now() - h.at < RefDetails.TTL_MS) return false;
    const f = this.failedAt.get(r.key);
    return f === undefined || this.now() - f >= RefDetails.RETRY_MS;
  }

  /** Ask for those of `reqs` not held, BATCH per request; resolves once every answer is in. */
  async load(run: RunPartial, reqs: readonly RefRequest[]): Promise<void> {
    const seen = new Set<string>();
    const todo = reqs.filter((r) => !seen.has(r.key) && (seen.add(r.key), this.wanted(r)));
    const batches: RefRequest[][] = [];
    for (let i = 0; i < todo.length; i += RefDetails.BATCH) batches.push(todo.slice(i, i + RefDetails.BATCH));
    await Promise.all(batches.map((b) => this.ask(run, b)));
  }

  private async ask(run: RunPartial, batch: RefRequest[]) {
    for (const r of batch) this.asking.add(r.key);
    const q = refDetailsQuery(batch);
    try {
      const res = await run(q.query, q.variables);
      const at = this.now();
      const got = q.read(res.data as Record<string, unknown> | null, res.errors ?? []);
      for (const [key, detail] of got) {
        this.held.delete(key);
        this.held.set(key, { detail, at });
        this.failedAt.delete(key);
      }
      for (const r of batch) if (!got.has(r.key)) this.failedAt.set(r.key, at);
      while (this.held.size > RefDetails.MAX) this.held.delete(this.held.keys().next().value!);
    } catch {
      const at = this.now();
      for (const r of batch) this.failedAt.set(r.key, at);
    } finally {
      for (const r of batch) this.asking.delete(r.key);
    }
  }
}
